<template>
    <Modal close-esc-event="keydown" :close-button="false" modal-class="m-auto border-0" size="max-w-4xl" mask-class="grid" @opening="onOpen" @closing="onClose">
        <template #toggle="scope">
            <button
                type="button"
                class="inline-flex items-center justify-center h-12 w-12 text-primary-700 hover:text-primary-800 transition-colors"
                :title="$t('views.reports.selectContentOptionsPage.header')"
                :aria-label="$t('views.reports.selectContentOptionsPage.header')"
                @click="scope.open"
            >
                <Icon :src="IconSource.ContentOptions" class="w-8 h-8"></Icon>
            </button>
        </template>

        <template #header="scope">
            <CloseModalButton @close="scope.close"></CloseModalButton>
        </template>

        <template #body="scope">
            <div class="bg-white py-6 px-8 rounded-xl w-[900px] max-w-[50vw]">
                <h2 class="text-xl font-semibold mb-6">{{ $t('views.reports.selectContentOptionsPage.header') }}</h2>
                <div class="flex justify-center">
                    <ContentOptionsSelector v-if="localState" :state="localState" :content-options="state.contentOptions" :auto-select-all="true" max-height-class="max-h-[53rem]"></ContentOptionsSelector>
                </div>
                <div class="flex space-x-4 mt-6">
                    <CustomButton class="flex-1" color-preset="secondary" @click="onCancel(scope.close)">
                        {{ $t('views.reports.reportsPage.approveDifferenceModal.cancelButton') }}
                    </CustomButton>
                    <CustomButton class="flex-1" :disabled="isSaveDisabled" @click="onSave(scope.close)">
                        {{ $t('views.reports.reportsPage.approveDifferenceModal.saveButton') }}
                    </CustomButton>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Modal } from '@makeabledk/vue-ui/components/modal';
import CustomButton from '@/components/ui/CustomButton.vue';
import CloseModalButton from '@/components/common/CloseModalButton.vue';
import ContentOptionsSelector from '@/views/dashboard/reports/selectContentOptions/ContentOptionsSelector.vue';
import Icon from '@/components/icons/Icon.vue';
import { IconSource, ReportContentOption, ReportViewState } from '@/types';

export default defineComponent({
    components: { Modal, CustomButton, CloseModalButton, ContentOptionsSelector, Icon },
    props: {
        state: {
            type: Object as () => ReportViewState,
            required: true,
        },
    },
    data() {
        return {
            IconSource,
            localState: null as ReportViewState | null,
        };
    },
    computed: {
        isSaveDisabled(): boolean {
            return !this.localState || this.localState.contentOptionIds.length < 1;
        },
    },
    methods: {
        onOpen() {
            this.localState = {
                ...this.$props.state,
                contentOptionIds: [...this.$props.state.contentOptionIds],
                contentOptionsTouched: this.$props.state.contentOptionsTouched,
            };
            this.applyDefaultSelection();
        },
        onClose() {
            this.localState = null;
        },
        applyDefaultSelection() {
            if (!this.localState || this.localState.contentOptionIds.length || this.localState.contentOptionsTouched) {
                return;
            }

            const activeIds = this.$props.state.contentOptions.filter((option: ReportContentOption) => option.is_active).map((option) => option.id);
            if (!activeIds.length) {
                return;
            }
            this.localState.contentOptionIds.splice(0, this.localState.contentOptionIds.length, ...activeIds);
        },
        onCancel(closeModal: () => void) {
            closeModal();
        },
        onSave(closeModal: () => void) {
            if (!this.localState) {
                return;
            }

            this.$props.state.contentOptionIds.splice(0, this.$props.state.contentOptionIds.length, ...this.localState.contentOptionIds);
            this.$props.state.contentOptionsTouched = this.$props.state.contentOptionsTouched || this.localState.contentOptionsTouched;
            closeModal();
        },
    },
});
</script>
